import type { RenderBoxEffects } from '@lite-workspace/shared/render-model';

// tiny-skia's 3-pass box blur approximates a gaussian at radius = round(sigma * 1.5) — mirrors
// engine/psd-render/core/src/shadow.rs `blur_radius_for`.
function blurRadiusFor(sigma: number): number {
  return Math.max(1, Math.round(sigma * 1.5));
}

/**
 * How far a single shadow/glow can bleed past its source shape's drawable bounds, in the same px unit
 * as `blur`/`spread`/`offsetX`/`offsetY`. Exact port of `shadow.rs::draw_shadow`'s `reach` (lines 17-24):
 * PS Spread dilates the alpha by `spread%·blur` first, then a 3-pass box blur runs over the remainder
 * (`sigma`); `+1` covers the dilate/blur AA fringe. `sigma === 0` (spread consumed the whole blur) skips
 * the blur term entirely, matching the Rust `if sigma > 0.0` gate — it is NOT `3 * blurRadiusFor(0)`.
 */
export function shadowReachPx(blur: number, spread: number, offsetX = 0, offsetY = 0): number {
  const spreadFraction = Math.min(1, Math.max(0, spread / 100));
  const spreadRadius = Math.round(blur * spreadFraction);
  const sigma = Math.max(0, (blur * (1 - spreadFraction)) / 2);
  const blurReach = sigma > 0 ? 3 * blurRadiusFor(sigma) : 0;
  const reach = spreadRadius + blurReach + 1;
  return reach + Math.max(Math.abs(offsetX), Math.abs(offsetY));
}

/**
 * Max bleed a box's shadows/glows/strokes can spill past its drawable rect. A region buffer must pad by
 * this or a soft shadow/glow near the region edge clips. Glows have no offset (only shadows do); strokes
 * are geometric (no blur), so their bleed is just the stroke width.
 */
export function effectBleed(effects: RenderBoxEffects | undefined): number {
  let pad = 0;
  for (const s of effects?.shadows ?? []) pad = Math.max(pad, shadowReachPx(s.blur, s.spread ?? 0, s.offsetX, s.offsetY));
  for (const g of effects?.glows ?? []) pad = Math.max(pad, shadowReachPx(g.blur, g.choke ?? 0));
  for (const st of effects?.strokes ?? []) pad = Math.max(pad, st.width);
  const bevel = effects?.bevel;
  if (bevel && bevel.enabled !== false && bevel.size > 0) {
    // Outer relief spills beyond the silhouette; match the native bevel buffer's conservative fringe.
    pad = Math.max(pad, Math.ceil(bevel.size + Math.max(0, bevel.soften ?? 0)) + 4);
  }

  return pad + 2;
}
